/**
 * SubjectRankings — WUA Subject × Country outcomes-revealed ranking page.
 *
 * Positions our leaderboard as the answer to "which uni is #1 for
 * [subject] in [country]?" without any dependency on QS/THE opinion
 * surveys. Composite driven by verified per-uni-per-subject median
 * salary + value-for-money + admissibility + satisfaction + retention.
 *
 * Empty state — if a country × subject has fewer than 8 unis with
 * verified per-row salary, we DON'T publish a ranking. We show a
 * transparency panel explaining that instead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, ExternalLink, Info } from "lucide-react";
import { subjectSlug, findSubjectBySlug, COUNTRY_META } from "@/lib/rankingSlug";
import { COUNTRY_BLURBS } from "@/lib/countryBlurbs";
import { COHORT_BLURBS } from "@/lib/cohortBlurbs";

const COUNTRY_LABEL = {
  UK: "United Kingdom", US: "United States", AU: "Australia", CA: "Canada",
  NL: "Netherlands", FR: "France", DE: "Germany", IE: "Ireland",
  IT: "Italy", ES: "Spain", DK: "Denmark", SE: "Sweden", NO: "Norway",
  NORDICS: "the Nordic Cluster",
};

// Curated elite-institution cohorts per country. Metadata mirrored
// from the backend ELITE_COHORTS map so the toggle UI can render the
// cohort name + members-note without an extra API call.
const ELITE_COHORTS = {
  UK: {
    key: "russell-group",
    label: "Russell Group",
    members_note: "Cambridge · Oxford · Imperial · UCL · LSE · Edinburgh · KCL · Manchester · Warwick · Bristol · Durham · + 13 more",
  },
  US: {
    key: "ivy-plus",
    label: "Ivy Plus",
    members_note: "Harvard · Yale · Princeton · Columbia · Penn · Brown · Cornell · Dartmouth · MIT · Stanford · Chicago · Duke · Northwestern · JHU · Caltech",
  },
  AU: {
    key: "go8",
    label: "Group of Eight",
    members_note: "Melbourne · Sydney · ANU · UNSW · Monash · UQ · UWA · Adelaide",
  },
  CA: {
    key: "u15",
    label: "U15",
    members_note: "Toronto · McGill · UBC · McMaster · Alberta · Montréal · Waterloo · Western · Calgary · Queen's · Ottawa · Manitoba · Laval · Dalhousie · Saskatchewan",
  },
  FR: {
    key: "grandes-ecoles",
    label: "Grandes Écoles",
    members_note: "Polytechnique · CentraleSupélec · Ponts · ENS · HEC · ESSEC · ESCP · EDHEC · emlyon · Sciences Po · INSEAD · PSL",
  },
};
const SIGNAL_LABEL = {
  outcomes: "Verified median salary (per subject)",
  value_for_money: "Salary ÷ intl tuition ratio",
  admissibility: "1 − acceptance rate",
  satisfaction: "Student satisfaction (NSS / QILT)",
  completion: "Completion + retention",
};

export default function SubjectRankings() {
  const [params, setParams] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();

  // Two entry paths supported:
  //   /rankings/:countrySlug/:subjectSlug  (SEO canonical, preferred)
  //   /rankings/:countrySlug               (SEO country landing)
  //   /rankings?country=UK&subject=...     (legacy, still handled)
  const countryFromRoute = routeParams.countrySlug
    ? COUNTRY_META[routeParams.countrySlug.toLowerCase()]?.code
    : null;
  const country = countryFromRoute || params.get("country") || "UK";

  const [index, setIndex] = useState(null);
  const [indexError, setIndexError] = useState(false);
  const [indexTry, setIndexTry] = useState(0);
  const [ranking, setRanking] = useState(null);
  const [rankingError, setRankingError] = useState(false);
  const [rankingTry, setRankingTry] = useState(0);
  const rankingReq = useRef(0);
  const [nssRanking, setNssRanking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eliteCohortOn, setEliteCohortOn] = useState(false);
  // v9.66 — "Rank by student experience (HESA NSS 2025)" toggle.
  // UK-only. When on, we swap the composite table for a pure-NSS
  // leaderboard fetched from /methodology/nss-2025/subject-ranking.
  const [nssModeOn, setNssModeOn] = useState(false);

  // Which cohort (if any) is available for the currently-picked
  // country. When null, the toggle row is hidden.
  const eliteCohortForCountry = ELITE_COHORTS[country] || null;

  // Resolve subject slug against the index once loaded. Also handles
  // canonical redirects — if the URL slug is a competitor variant
  // like `economics-best`, `best-economics`, or `top-economics-uni`,
  // strip the fluff and canonical-rewrite to the real slug so
  // Google doesn't split PageRank across URL variants.
  const subject = useMemo(() => {
    if (routeParams.subjectSlug && index?.countries?.[country]) {
      const subs = index.countries[country].map((r) => r.subject);
      const raw = routeParams.subjectSlug.toLowerCase();
      const exact = findSubjectBySlug(raw, subs);
      if (exact) return exact;
      const stripped = raw
        .replace(/^(best|top|rankings?|university|universities|unis?|uni|for)-/, "")
        .replace(/-(best|top|rankings?|university|universities|unis?|uni|uk|us|au|ca|nl|fr|de|ie|it|es|dk|se|no)$/g, "")
        .replace(/-(uk|us|au|ca|nl|fr|de|ie|it|es|dk|se|no)$/g, "");
      if (stripped && stripped !== raw) {
        const guess = findSubjectBySlug(stripped, subs);
        if (guess) {
          navigate(`/rankings/${country.toLowerCase()}/${subjectSlug(guess)}`, { replace: true });
          return guess;
        }
      }
      return "";
    }
    return params.get("subject") || "";
  }, [routeParams.subjectSlug, index, country, params, navigate]);

  // A failed index load is an error with a retry, never "0 published subjects".
  useEffect(() => {
    let live = true;
    setIndexError(false);
    api.get("/rankings/subject-index")
      .then((r) => { if (live) setIndex(r.data); })
      .catch(() => { if (live) setIndexError(true); });
    return () => { live = false; };
  }, [indexTry]);
  const indexReady = !!index;

  const subjectsForCountry = useMemo(() => {
    if (!index?.countries?.[country]) return [];
    return index.countries[country].map((r) => r.subject);
  }, [index, country]);

  // v9.63: Hydrate the elite-cohort toggle from the URL slug so
  // `/rankings/uk/economics/russell-group` opens the filtered view
  // directly (SEO landing). And keep the URL in sync when the user
  // flips the toggle so bookmarks work.
  useEffect(() => {
    const slug = routeParams.cohortSlug || "";
    if (slug && eliteCohortForCountry && slug === eliteCohortForCountry.key) {
      if (!eliteCohortOn) setEliteCohortOn(true);
    } else if (!slug && eliteCohortOn) {
      setEliteCohortOn(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams.cohortSlug, eliteCohortForCountry?.key]);

  useEffect(() => {
    // Reflect the cohort toggle in the URL so users can share the
    // filtered leaderboard. Only touch when we have a subject
    // (otherwise the base country page carries no cohort segment).
    if (!subject || !eliteCohortForCountry) return;
    const base = `/rankings/${country.toLowerCase()}/${subjectSlug(subject)}`;
    const target = eliteCohortOn ? `${base}/${eliteCohortForCountry.key}` : base;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eliteCohortOn, subject, country]);

  useEffect(() => {
    if (!subject) {
      setRanking(null);
      setNssRanking(null);
      return;
    }
    setLoading(true);
    setRankingError(false);
    const req = ++rankingReq.current;
    const cohortFlag = eliteCohortOn && eliteCohortForCountry
      ? `&elite_cohort=${eliteCohortForCountry.key}`
      : "";
    const composite = api
      .get(`/rankings/subject?country=${country}&subject=${encodeURIComponent(subject)}&limit=50${cohortFlag}`)
      .then((r) => { if (req === rankingReq.current) setRanking(r.data); })
      .catch(() => { if (req === rankingReq.current) { setRanking(null); setRankingError(true); } });
    // Also fetch the pure-NSS cut when we're on UK — cheap query,
    // avoids a spinner when the user flips the toggle.
    let nssPromise = Promise.resolve();
    if (country === "UK") {
      nssPromise = api
        .get(`/methodology/nss-2025/subject-ranking?subject=${encodeURIComponent(subject)}&limit=50`)
        .then((r) => { if (req === rankingReq.current) setNssRanking(r.data); })
        .catch(() => { if (req === rankingReq.current) setNssRanking(null); });
    } else {
      setNssRanking(null);
    }
    Promise.all([composite, nssPromise]).finally(() => { if (req === rankingReq.current) setLoading(false); });
  }, [country, subject, eliteCohortOn, eliteCohortForCountry, rankingTry]);

  // Auto-reset the elite-cohort toggle when the user switches to a
  // country that doesn't have one.
  useEffect(() => {
    if (!eliteCohortForCountry && eliteCohortOn) setEliteCohortOn(false);
  }, [eliteCohortForCountry, eliteCohortOn]);

  // Auto-reset the NSS-mode pill when we switch off UK.
  useEffect(() => {
    if (country !== "UK" && nssModeOn) setNssModeOn(false);
  }, [country, nssModeOn]);

  // Set the browser tab + meta description for SEO. React-helmet
  // isn't installed but document.title works fine and Googlebot
  // reads dynamically-rendered titles for indexed pages.
  useEffect(() => {
    const cohortLbl = eliteCohortOn && eliteCohortForCountry ? ` ${eliteCohortForCountry.label}` : "";
    if (subject) {
      document.title = `Best${cohortLbl} ${subject} universities in ${COUNTRY_LABEL[country]} · WUA Outcomes Ranking`;
    } else if (country) {
      document.title = `${COUNTRY_LABEL[country]} university subject rankings · WichUniversityAdvisor`;
    }
  }, [country, subject, eliteCohortOn, eliteCohortForCountry]);

  // Google-facing structured data — ItemList schema so the ranked
  // universities render as a rich-result carousel in search. Only
  // ships once the ranking has 5+ real rows so we never emit empty
  // schema.
  useEffect(() => {
    const existing = document.getElementById("wua-ranking-jsonld");
    if (existing) existing.remove();
    if (!ranking?.rows || ranking.rows.length < 5) return;
    const items = ranking.rows.slice(0, 20).map((r) => ({
      "@type": "ListItem",
      position: r.rank,
      url: `https://whichuniversityadvisor.com/uni/${r.university_id}`,
      name: r.name,
    }));
    const payload = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: ranking.rows.length,
      name: `Best ${ranking.subject} universities in ${COUNTRY_LABEL[country]} — WUA outcomes-revealed ranking`,
      description: (
        `Verified outcomes-driven leaderboard of ${ranking.unis_qualified} ` +
        `${COUNTRY_LABEL[country]} universities for ${ranking.subject}. Ranked on ` +
        `verified median graduate salary, value for money, admissibility, satisfaction ` +
        `and completion — never opinion surveys or licensed prestige scores.`
      ),
      itemListElement: items,
    };
    const s = document.createElement("script");
    s.id = "wua-ranking-jsonld";
    s.type = "application/ld+json";
    s.textContent = JSON.stringify(payload);
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, [ranking, country]);

  const setCountry = (c) => navigate(`/rankings/${c.toLowerCase()}`);
  const setSubject = (s) => {
    const base = `/rankings/${country.toLowerCase()}/${subjectSlug(s)}`;
    const cohortSeg = eliteCohortOn && eliteCohortForCountry
      ? `/${eliteCohortForCountry.key}`
      : "";
    navigate(base + cohortSeg);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="subject-rankings-page">
      <div className="mb-8">
        <div className="label-uppercase text-[#4C1D95]">WUA Outcomes-Revealed Ranking</div>
        <h1 className="mt-2 font-editorial text-4xl sm:text-5xl text-[#002147]">
          Subject × Country rankings, driven by <span className="italic">what employers actually pay</span>.
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl">
          No opinion polls. No licensed prestige scores. Just verified per-uni-per-subject
          median graduate salary, value for money, admissibility and student satisfaction —
          combined into one composite where every input is on the page next to the rank.
        </p>
      </div>

      {/* Country + subject pickers */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="country-picker">
        {Object.entries(COUNTRY_LABEL).map(([code, label]) => (
          <button
            key={code}
            onClick={() => setCountry(code)}
            data-testid={`country-${code}`}
            className={
              "px-4 py-2 rounded-full border text-sm font-underground-sc tracking-wider transition " +
              (country === code
                ? "bg-[#002147] text-white border-[#002147]"
                : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {subjectsForCountry.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="subject-picker">
          {subjectsForCountry.map((s) => (
            <button
              key={s}
              onClick={() => setSubject(s)}
              data-testid={`subject-${s}`}
              className={
                "px-3 py-1.5 rounded-full border text-[13px] transition " +
                (subject === s
                  ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                  : "bg-white text-stone-700 border-stone-200 hover:border-[#4C1D95]")
              }
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {!subject && COUNTRY_BLURBS[country] && (
        <div
          className="mb-8 rounded-md border border-[#4C1D95]/25 bg-[#F5F0FF] p-5 sm:p-6"
          data-testid={`country-blurb-${country}`}
        >
          <p className="font-editorial text-lg sm:text-xl text-[#002147] leading-snug">
            {COUNTRY_BLURBS[country].headline}
          </p>
          <p className="mt-3 text-sm text-stone-700 leading-relaxed">
            {COUNTRY_BLURBS[country].body}
          </p>
          <div className="mt-3 text-[10px] font-underground-sc tracking-wider text-[#4C1D95]">
            Sources · {COUNTRY_BLURBS[country].source}
          </div>
        </div>
      )}

      {eliteCohortForCountry && !(indexReady && subjectsForCountry.length === 0) && (
        <div className="flex items-start sm:items-center gap-3 mb-6 flex-col sm:flex-row" data-testid={`elite-cohort-toggle-${country}`}>
          <button
            type="button"
            role="switch"
            aria-checked={eliteCohortOn}
            onClick={() => setEliteCohortOn((v) => !v)}
            data-testid="elite-cohort-switch"
            className={
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 " +
              (eliteCohortOn ? "bg-[#4C1D95]" : "bg-stone-300")
            }
          >
            <span
              className={
                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform " +
                (eliteCohortOn ? "translate-x-6" : "translate-x-1")
              }
            />
          </button>
          <label
            onClick={() => setEliteCohortOn((v) => !v)}
            className="text-sm text-stone-700 cursor-pointer select-none"
          >
            <span className="font-semibold text-[#002147]">{eliteCohortForCountry.label} only</span>
            <span className="ml-2 text-stone-500 text-[12px]">
              {eliteCohortForCountry.members_note}
            </span>
          </label>
        </div>
      )}

      {!subject && !indexReady && !indexError && (
        <div className="animate-pulse rounded-md border border-stone-200 bg-stone-50 p-6 text-sm text-stone-500" data-testid="subjects-loading">
          Loading published subjects…
        </div>
      )}
      {!indexReady && indexError && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-6 text-sm text-stone-700" data-testid="subjects-error">
          We couldn't load the list of published rankings just now.{" "}
          <button type="button" className="underline font-semibold" onClick={() => setIndexTry((n) => n + 1)} data-testid="subjects-retry">
            Try again
          </button>
        </div>
      )}
      {!subject && indexReady && subjectsForCountry.length > 0 && (
        <div className="rounded-md border border-stone-200 bg-stone-50 p-6" data-testid="empty-hint">
          <p className="text-sm text-stone-700">
            Pick a subject above to see the {COUNTRY_LABEL[country]} ranking.
            We currently publish {subjectsForCountry.length} rankable subjects for {COUNTRY_LABEL[country]}
            (subjects need at least 8 unis with verified per-row salary to be published).
          </p>
        </div>
      )}
      {indexReady && subjectsForCountry.length === 0 && (
        <div className="rounded-md border border-stone-200 bg-stone-50 p-6" data-testid="no-published-rankings">
          <p className="text-sm text-stone-700">
            We don't publish any {COUNTRY_LABEL[country]} subject rankings at the moment. A subject ranking needs at
            least {index.min_unis || 8} universities whose graduate salary for that subject is verified against an
            official per-university source, and {COUNTRY_LABEL[country]} doesn't currently have enough verified
            figures for any subject. We'd rather show nothing than rank on unverified salaries.
          </p>
        </div>
      )}

      {subject && !loading && rankingError && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-6 text-sm text-stone-700" data-testid="ranking-error">
          We couldn't load this ranking just now.{" "}
          <button type="button" className="underline font-semibold" onClick={() => setRankingTry((n) => n + 1)} data-testid="ranking-retry">
            Try again
          </button>
        </div>
      )}

      {subject && loading && (
        <div className="animate-pulse rounded-md border border-stone-200 bg-stone-50 p-6 text-stone-500">
          Computing composite…
        </div>
      )}

      {subject && !loading && ranking && (
        <>
          {eliteCohortOn && eliteCohortForCountry && COHORT_BLURBS[eliteCohortForCountry.key] && (
            <div
              className="mb-6 rounded-md border border-amber-300/50 bg-amber-50/40 p-5"
              data-testid={`cohort-blurb-${eliteCohortForCountry.key}`}
            >
              <div className="label-uppercase text-amber-900 mb-1">
                {COHORT_BLURBS[eliteCohortForCountry.key].headline}
              </div>
              <p className="text-sm text-stone-800 leading-relaxed">
                {COHORT_BLURBS[eliteCohortForCountry.key].body}
              </p>
            </div>
          )}

          {country === "UK" && nssRanking && nssRanking.rows && nssRanking.rows.length >= 5 && (
            <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="ranking-mode-toggle">
              <button
                type="button"
                onClick={() => setNssModeOn(false)}
                data-testid="ranking-mode-outcomes"
                className={
                  "px-4 py-1.5 rounded-full border text-[13px] font-underground-sc tracking-wider transition " +
                  (!nssModeOn
                    ? "bg-[#002147] text-white border-[#002147]"
                    : "bg-white text-stone-700 border-stone-300 hover:border-[#002147]")
                }
              >
                Rank by outcomes composite
              </button>
              <button
                type="button"
                onClick={() => setNssModeOn(true)}
                data-testid="ranking-mode-nss"
                className={
                  "px-4 py-1.5 rounded-full border text-[13px] font-underground-sc tracking-wider transition inline-flex items-center gap-2 " +
                  (nssModeOn
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-emerald-800 border-emerald-600/50 hover:border-emerald-700")
                }
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Rank by student experience · HESA NSS 2025
              </button>
              <div className="text-[11px] text-stone-500 font-mono">
                {nssModeOn
                  ? `Pure NSS Q26 positivity — ${nssRanking.n_qualified} unis`
                  : "5-signal composite (salary · value · admiss · satisfaction · retention)"}
              </div>
            </div>
          )}

          {country === "UK" && nssModeOn && nssRanking
            ? <NssRankingTable ranking={nssRanking} subject={subject} />
            : <RankingTable ranking={ranking} country={country} />
          }
        </>
      )}

      {/* Methodology panel — permanent, not collapsed */}
      <MethodologyPanel />
    </div>
  );
}

function RankingTable({ ranking, country }) {
  if (!ranking?.rows?.length) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-6" data-testid="empty-ranking">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-800 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">No leaderboard published for this pairing.</p>
            <p className="mt-1 text-sm text-amber-900/80">
              {ranking.unis_qualified ?? 0} unis passed the verified-salary filter (we need
              at least {ranking.min_required ?? 8} to publish a ranking). Rather than show
              a 3-uni leaderboard that misleads, we suppress the page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-stone-200 overflow-hidden" data-testid="ranking-table">
      <div className="bg-[#002147] text-white px-5 py-4 flex flex-wrap items-baseline gap-3">
        <Trophy className="h-4 w-4" />
        <div className="font-mono text-sm uppercase tracking-wider">
          #1 for {ranking.subject} · {COUNTRY_LABEL[country]} · {ranking.cycle}
          {ranking.cohort && (
            <span className="ml-2 text-amber-300">
              · {ranking.cohort.label} cut
            </span>
          )}
        </div>
        <div className="ml-auto text-[11px] text-stone-300 font-mono">
          {ranking.unis_qualified} unis qualified
          {ranking.cohort && ranking.cohort.national_pool_size && (
            <span className="text-amber-300">
              {" "}(of {ranking.cohort.national_pool_size} nationally)
            </span>
          )}
          {" "}· ceiling salary
          &nbsp;${Number(ranking.salary_ceiling_usd).toLocaleString()}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-wider text-stone-600">
          <tr>
            <th className="p-3 text-left">#</th>
            <th className="p-3 text-left">University</th>
            <th className="p-3 text-right">Score</th>
            <th className="p-3 text-right">Median Salary</th>
            <th className="p-3 text-right">Intl Tuition</th>
            <th className="p-3 text-right">Accept</th>
            <th className="p-3 text-right">QS #</th>
          </tr>
        </thead>
        <tbody>
          {ranking.rows.map((r) => (
            <RankingRow key={r.university_id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingRow({ row }) {
  const [open, setOpen] = useState(false);
  const rank = row.rank;
  const isTop = rank <= 3;
  return (
    <>
      <tr
        data-testid={`ranking-row-${row.university_id}`}
        className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <td className={"p-3 font-mono " + (isTop ? "text-amber-600 font-bold" : "text-stone-600")}>
          <div>#{rank}</div>
          {row.rank_national && row.rank_national !== rank && (
            <div
              className="text-[10px] uppercase tracking-wider text-stone-400 mt-0.5"
              title="Rank in the full country pool (not the elite cohort)"
              data-testid={`rank-national-${row.university_id}`}
            >
              nat · #{row.rank_national}
            </div>
          )}
          {row.rank_national && row.rank_national === rank && (
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 mt-0.5">
              nat · #{row.rank_national}
            </div>
          )}
        </td>
        <td className="p-3">
          <Link
            to={`/uni/${row.university_id}`}
            className="text-[#002147] hover:underline"
            onClick={(e) => e.stopPropagation()}
            data-testid={`ranking-uni-link-${row.university_id}`}
          >
            {row.name}
          </Link>
          <div className="text-[11px] text-stone-500 font-mono mt-0.5">
            source: {row.salary_source}
          </div>
        </td>
        <td className="p-3 text-right font-mono font-bold text-[#4C1D95]">{row.score}</td>
        <td className="p-3 text-right font-mono">
          ${row.median_salary_usd ? Number(row.median_salary_usd).toLocaleString() : "—"}
        </td>
        <td className="p-3 text-right font-mono">
          ${row.tuition_intl_usd ? Number(row.tuition_intl_usd).toLocaleString() : "—"}
        </td>
        <td className="p-3 text-right font-mono">
          {row.acceptance_rate ? `${Math.round(row.acceptance_rate * 100)}%` : "—"}
        </td>
        <td className="p-3 text-right font-mono text-stone-500">
          {row.world_rank_qs ? `#${row.world_rank_qs}` : "—"}
        </td>
      </tr>
      {open && (
        <tr className="bg-stone-50 border-t border-stone-100" data-testid={`ranking-breakdown-${row.university_id}`}>
          <td colSpan={7} className="p-4">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-2">
              Sub-scores + renormalised weights
            </div>
            <div className="grid sm:grid-cols-5 gap-3">
              {Object.entries(row.sub_scores).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] text-stone-600">{SIGNAL_LABEL[k]}</div>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="font-mono text-lg">
                      {v == null ? "—" : v}
                    </span>
                    <span className="text-[10px] font-mono text-stone-400">
                      /100 · {Math.round((row.applied_weights[k] || 0) * 100)}% weight
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-stone-200 overflow-hidden">
                    <div className="h-full bg-[#4C1D95]" style={{ width: `${v || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function NssRankingTable({ ranking, subject }) {
  if (!ranking?.rows?.length) return null;
  return (
    <div className="rounded-md border border-emerald-200 overflow-hidden" data-testid="nss-ranking-table">
      <div className="bg-emerald-800 text-white px-5 py-4 flex flex-wrap items-baseline gap-3">
        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] uppercase tracking-wider font-mono">
          HESA verified
        </span>
        <div className="font-mono text-sm uppercase tracking-wider">
          Student experience #1 for {subject} · {ranking.cycle}
        </div>
        <div className="ml-auto text-[11px] text-emerald-100 font-mono">
          {ranking.n_qualified} unis · Q26 positivity {ranking.resolved_to && (
            <span className="ml-1 text-emerald-200">· resolved to CAH: {ranking.resolved_to}</span>
          )}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-emerald-50 text-[10px] font-underground-sc tracking-wider text-emerald-900">
          <tr>
            <th className="p-3 text-left">#</th>
            <th className="p-3 text-left">University</th>
            <th className="p-3 text-right">Q26 positivity</th>
            <th className="p-3 text-right">HESA benchmark</th>
            <th className="p-3 text-right">Δ vs benchmark</th>
            <th className="p-3 text-right">Responses</th>
          </tr>
        </thead>
        <tbody>
          {ranking.rows.map((r) => {
            const delta = r.benchmark_pct != null ? Math.round((r.positivity_pct - r.benchmark_pct) * 10) / 10 : null;
            const deltaColor = delta == null ? "text-stone-400" : delta >= 0 ? "text-emerald-700" : "text-red-700";
            return (
              <tr key={r.uni_id} className="border-t border-stone-100 hover:bg-emerald-50/40" data-testid={`nss-ranking-row-${r.uni_id}`}>
                <td className={"p-3 font-mono " + (r.rank <= 3 ? "text-amber-600 font-bold" : "text-stone-600")}>
                  #{r.rank}
                </td>
                <td className="p-3">
                  <Link
                    to={`/uni/${r.uni_id}`}
                    className="text-[#002147] hover:underline"
                    data-testid={`nss-ranking-uni-link-${r.uni_id}`}
                  >
                    {r.name}
                  </Link>
                  {r.cah_name && (
                    <div className="text-[11px] text-stone-500 font-mono mt-0.5">
                      CAH · {r.cah_name}
                    </div>
                  )}
                </td>
                <td className="p-3 text-right font-mono font-bold text-emerald-800">
                  {r.positivity_pct}%
                </td>
                <td className="p-3 text-right font-mono text-stone-500">
                  {r.benchmark_pct != null ? `${r.benchmark_pct}%` : "—"}
                </td>
                <td className={"p-3 text-right font-mono font-semibold " + deltaColor}>
                  {delta == null ? "—" : (delta >= 0 ? `+${delta}` : delta) + " pts"}
                </td>
                <td className="p-3 text-right font-mono text-stone-500">
                  {r.n_responses ? Number(r.n_responses).toLocaleString() : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-5 py-3 bg-emerald-50/60 border-t border-emerald-100 text-[11px] text-emerald-900 font-mono">
        Source · HESA NSS 2025 · CAH_level_1 · Q26 “Overall satisfaction with course” positivity.
        Ordered purely by positivity — no salary, no tuition, no admissibility mixed in.
      </div>
    </div>
  );
}

function MethodologyPanel() {
  return (
    <div className="mt-12 rounded-md border border-stone-200 bg-white p-6" data-testid="methodology-panel">
      <div className="label-uppercase text-[#4C1D95] mb-3">What #1 means here</div>
      <p className="text-sm text-stone-700 leading-relaxed">
        <strong>#1 for a subject × country</strong> means: of the universities we have
        verified per-uni-per-subject median graduate salary for, this uni scored highest on
        a weighted composite of outcomes, value for money, admissibility, satisfaction and
        retention. It is <em>not</em> "the best university" — that's a decision, not a
        number. It <em>is</em> the strongest outcomes-revealed signal we can honestly
        publish with the data we own.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="font-semibold text-[#002147]">What we use</div>
          <ul className="mt-1 space-y-1 text-stone-700 list-disc pl-5">
            <li>35% — verified median salary per uni × subject (HESA/LEO, Scorecard, QILT per-uni, StatCan per-uni). Never tier-mapped.</li>
            <li>20% — salary ÷ intl tuition ratio</li>
            <li>15% — 1 − offer/acceptance rate</li>
            <li>15% — HESA NSS (UK) / QILT (AU) uni-level satisfaction score</li>
            <li>15% — completion + first-year retention (where published)</li>
          </ul>
        </div>
        <div>
          <div className="font-semibold text-[#002147]">What we don't use</div>
          <ul className="mt-1 space-y-1 text-stone-700 list-disc pl-5">
            <li>QS/THE academic reputation surveys — we don't run one</li>
            <li>Employer reputation surveys — same</li>
            <li>Citations / bibliometrics — we don't ingest OpenAlex at scale today</li>
            <li>Uni's external QS `world_rank` — shown for context only, never weighted</li>
          </ul>
        </div>
      </div>
      <p className="mt-4 text-[11px] font-mono text-stone-500">
        Weights renormalise over the signals present on each uni so a uni missing
        satisfaction data still ranks fairly against one that has it. Every input is on
        the page — click any row to see the breakdown.
        <Link to="/methodology" className="ml-2 underline inline-flex items-center gap-1">
          Full methodology <ExternalLink className="h-3 w-3" />
        </Link>
      </p>
    </div>
  );
}
